import { useCallback, useEffect, useMemo, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { NetworkStatus } from '@apollo/client'

import { useStaleIncidentGroupsQuery } from '@/graphql/generated/hooks'

import {
  TEAM_STALE_GROUPS_PAGE_SIZE,
  TEAM_STALE_UNASSIGNED_GROUP_ID,
} from '../constants/constants'
import type { TeamStaleGroup } from '../types/types'
import { getStaleCutoff } from '../utils/utils'

interface UseTeamStaleGroupsIProps {
  isOpen: boolean
}

interface UseTeamStaleGroupsResultIProps {
  groups: TeamStaleGroup[]
  /** Org-wide number of GROUPS (not incidents), from the server aggregate. */
  totalGroupCount: number
  /**
   * The staleness cutoff this open snapshot uses. Shared with the per-group
   * detail queries and the program-resolve id scan so every request of one
   * modal session selects the same stale set.
   */
  cutoff: string
  /** More group pages exist server-side. */
  hasMore: boolean
  /** Appends the next page of groups; no-op while a page is in flight. */
  loadMore: () => void
  /** First page (or retry) in flight. */
  isLoading: boolean
  /** A subsequent group page in flight. */
  isLoadingMore: boolean
  isError: boolean
  refetch: () => void
}

/**
 * The team stale work modal's grouped list (ENG-32213): per-user stale
 * counts aggregated server-side (SQL GROUP BY on COALESCE(owner_id,
 * guard_id)), ordered worst-first, paged TEAM_STALE_GROUPS_PAGE_SIZE at a
 * time — modal open is one cheap request regardless of org size. This
 * replaces the ENG-32130 interim that scanned every stale incident and
 * grouped client-side. `first` is a zero-based PAGE INDEX, `last` the page
 * size (ENG-22054). Snapshots a fresh cutoff each time the modal opens and
 * is deliberately never refetched while open: group counts are server
 * aggregates, so a drawer resolve updates the loaded card via the
 * normalized cache but the counts only refresh on an explicit refetch
 * (bulk resolve does one).
 */
export const useTeamStaleGroups = ({
  isOpen,
}: UseTeamStaleGroupsIProps): UseTeamStaleGroupsResultIProps => {
  // Snapshot a fresh cutoff on each open transition, adjusted during render
  // (React's documented "storing information from previous renders" pattern):
  // it lands synchronously BEFORE the query hook below subscribes. A
  // useEffect would run after that render, so the first non-skipped render
  // would subscribe with the previous open's cutoff — one wasted request per
  // open, plus a cached flash of the previous list on re-open. State rather
  // than useMemo keyed on isOpen: React may discard memo caches, and a
  // mid-open recompute would swap the query variables and refire the query.
  const [cutoff, setCutoff] = useState(getStaleCutoff)
  const [isPrevOpen, setIsPrevOpen] = useState(isOpen)
  if (isOpen !== isPrevOpen) {
    setIsPrevOpen(isOpen)
    if (isOpen) setCutoff(getStaleCutoff())
  }

  const {
    data,
    loading: isQueryLoading,
    networkStatus,
    error,
    refetch,
    fetchMore,
  } = useStaleIncidentGroupsQuery({
    // Surfaces fetchMore progress through `networkStatus` instead of a
    // hand-rolled loading flag.
    notifyOnNetworkStatusChange: true,
    skip: !isOpen,
    variables: {
      actionUpdatedAtLte: cutoff,
      first: 0,
      last: TEAM_STALE_GROUPS_PAGE_SIZE,
    },
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { action: 'fetch_team_groups', feature: 'stale_work' },
      })
    }
  }, [error])

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const isLoading = isQueryLoading && !isLoadingMore

  const totalGroupCount = data?.staleIncidentGroups?.totalCount ?? 0

  const groups = useMemo(() => {
    // Deduped by attribution key: a mid-session resolve shifts the
    // count-ordered page boundaries, so a later page can re-return a group
    // an earlier one already delivered — duplicate React keys otherwise.
    const groupsByKey = new Map<string, TeamStaleGroup>()
    for (const edge of data?.staleIncidentGroups?.edges ?? []) {
      const node = edge?.node
      if (!node) continue
      const key = node.userId ?? TEAM_STALE_UNASSIGNED_GROUP_ID
      if (!groupsByKey.has(key)) groupsByKey.set(key, node)
    }
    return [...groupsByKey.values()]
  }, [data])

  // Progress is judged on unique groups, never raw accumulated edges,
  // mirroring useTeamStaleGroupIncidents: a duplicate edge from a boundary
  // shift must not count toward totalGroupCount, or hasMore can flip false
  // with a real group never loaded.
  const uniqueGroupCount = groups.length
  const hasMore = uniqueGroupCount > 0 && uniqueGroupCount < totalGroupCount

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return
    // Floor of the unique count re-requests the shifted boundary page after
    // count drift (a refetch replaces the edges with page 0 and the
    // derivation resets with it); the attribution-key dedupe above absorbs
    // any overlap.
    const nextPage = Math.floor(uniqueGroupCount / TEAM_STALE_GROUPS_PAGE_SIZE)
    fetchMore({
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.staleIncidentGroups) return previous
        return {
          ...fetchMoreResult,
          staleIncidentGroups: {
            ...fetchMoreResult.staleIncidentGroups,
            edges: [
              ...(previous.staleIncidentGroups?.edges ?? []),
              ...(fetchMoreResult.staleIncidentGroups.edges ?? []),
            ],
          },
        }
      },
      variables: { first: nextPage },
    }).catch((pageError) => {
      // fetchMore rejections do NOT flow into the hook's `error`, so report
      // them explicitly; the scroll sentinel stays mounted for another try.
      Sentry.captureException(pageError, {
        tags: { action: 'fetch_team_groups_page', feature: 'stale_work' },
      })
    })
  }, [fetchMore, hasMore, isLoading, isLoadingMore, uniqueGroupCount])

  // Apollo's refetch rejects on failure; the rejection is already surfaced
  // through the hook's `error` (and the Sentry effect above), so swallow it
  // to avoid an unhandled promise rejection from the retry button.
  const handleRefetch = useCallback(() => {
    refetch().catch(() => undefined)
  }, [refetch])

  return {
    cutoff,
    groups,
    hasMore,
    isError: Boolean(error),
    isLoading,
    isLoadingMore,
    loadMore,
    refetch: handleRefetch,
    totalGroupCount,
  }
}
